import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter, createHashHistory } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";

const serverUrl = import.meta.env.VITE_SERVER_URL || "http://localhost:3000";
const normalizedServerUrl = serverUrl.endsWith("/") ? serverUrl.slice(0, -1) : serverUrl;

const isDev = import.meta.env.MODE === "development" || !import.meta.env.VITE_SENTRY_DSN;

if (!isDev) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tunnel: `${normalizedServerUrl}/api/sentry-tunnel`,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    environment: import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE || "production",
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

import Loader from "./components/loader";
import GlobalErrorComponent from "./components/global-error-component";
import { routeTree } from "./routeTree.gen";
import { orpc, queryClient } from "./utils/orpc";

import { useTerminalStore } from "./store/terminal";

if (typeof window !== "undefined") {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("platform") === "desktop") {
    useTerminalStore.getState().setPlatform("desktop");
  }
}

const isDesktop =
  typeof window !== "undefined" &&
  (window.location.protocol === "views:" ||
    navigator.userAgent.toLowerCase().includes("electrobun") ||
    useTerminalStore.getState().platform === "desktop");

const router = createRouter({
  routeTree,
  history: isDesktop ? createHashHistory() : undefined,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultPendingComponent: () => <Loader />,
  defaultErrorComponent: ({ error, reset }) => <GlobalErrorComponent error={error} reset={reset} />,
  context: { orpc, queryClient, isDesktop },
  Wrap: function WrapComponent({ children }: { children: React.ReactNode }) {
    return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("app");

if (!rootElement) {
  throw new Error("Root element not found");
}

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <GlobalErrorComponent error={error} reset={resetError} />
      )}
    >
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>,
  );
}
